import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Edit2, Trash2, UserCheck, UserX, Search, Eye, Copy, ExternalLink, ArrowUpDown, Clock, Timer, Activity, RotateCcw, Link2, CheckSquare, Square, Download, FileSpreadsheet, Settings, RefreshCw, Database, Loader2, FileUser } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    department: "",
    role: "user",
    presentation_time_limit: 25,
    designation: "",
    tek_id: "",
    location: ""
  });
  
  // Tab for active vs deleted users
  const [activeTab, setActiveTab] = useState("active");
  
  // Main view tab (users or api-config)
  const [mainTab, setMainTab] = useState("users");
  
  // API Configuration state
  const [apiConfig, setApiConfig] = useState({
    enabled: false,
    endpoint_url: "",
    api_key: "",
    auth_type: "bearer", // bearer, api_key, basic
    field_mapping: {
      name: "name",
      email: "email",
      department: "department",
      designation: "title",
      tek_id: "employee_id",
      location: "location"
    },
    sync_interval: "manual", // manual, daily, weekly
    last_sync: null
  });
  const [loadingApiConfig, setLoadingApiConfig] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  
  // EVOLVE API (Outbound API) state
  const [evolveApi, setEvolveApi] = useState({
    enabled: false,
    api_keys: [],
    created_at: null
  });
  const [loadingEvolveApi, setLoadingEvolveApi] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState("");
  const [generatedKey, setGeneratedKey] = useState(null);
  
  // Activity Log Dialog
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Sorting and filtering
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Multi-select
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchDeletedUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/deleted/list`);
      setDeletedUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch deleted users:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await axios.put(`${API}/users/${selectedUser.id}`, formData);
        toast.success("User updated successfully");
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success("User created successfully");
      }
      setShowDialog(false);
      setSelectedUser(null);
      setFormData({ name: "", email: "", department: "", role: "user", presentation_time_limit: 25, designation: "", tek_id: "", location: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      department: user.department,
      role: user.role,
      presentation_time_limit: user.presentation_time_limit || 25,
      designation: user.designation || "",
      tek_id: user.tek_id || "",
      location: user.location || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success("User moved to deleted list");
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
      fetchDeletedUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const restoreUser = async (user) => {
    try {
      await axios.post(`${API}/users/${user.id}/restore`);
      toast.success("User restored successfully");
      fetchUsers();
      fetchDeletedUsers();
    } catch (error) {
      toast.error("Failed to restore user");
    }
  };

  const permanentDelete = async (user) => {
    try {
      await axios.delete(`${API}/users/${user.id}/permanent`);
      toast.success("User permanently deleted");
      fetchDeletedUsers();
    } catch (error) {
      toast.error("Failed to permanently delete user");
    }
  };

  const toggleStatus = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const copyUserLink = (user) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/u/${user.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Profile link copied to clipboard!");
  };

  const openUserPortal = (user) => {
    const baseUrl = window.location.origin;
    window.open(`${baseUrl}/u/${user.share_token}`, '_blank');
  };

  const viewUserActivity = async (user) => {
    setSelectedUser(user);
    setShowActivityDialog(true);
    setLoadingActivity(true);
    
    try {
      const response = await axios.get(`${API}/users/${user.id}/activity-log`);
      setActivityLogs(response.data || []);
    } catch (error) {
      console.error("Failed to fetch activity:", error);
      toast.error("Failed to load activity log");
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Multi-select functions
  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const bulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }
    
    try {
      await axios.post(`${API}/users/bulk-action`, {
        user_ids: selectedUsers,
        action: action
      });
      
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} completed for ${selectedUsers.length} users`);
      setSelectedUsers([]);
      fetchUsers();
      fetchDeletedUsers();
    } catch (error) {
      toast.error(`Bulk ${action} failed`);
    }
  };

  // Export to Excel (CSV)
  const exportToExcel = async () => {
    try {
      const response = await axios.get(`${API}/users/export`);
      const users = response.data.users;
      
      // Create CSV content
      const headers = ["Name", "Email", "Department", "Role", "Status", "Last Login", "Time Limit (min)", "Total Sessions", "Completed Sessions", "Avg Score"];
      const csvRows = [
        headers.join(","),
        ...users.map(u => [
          `"${u.name || ''}"`,
          `"${u.email || ''}"`,
          `"${u.department || ''}"`,
          `"${u.role || ''}"`,
          u.is_active ? "Active" : "Inactive",
          u.last_login ? new Date(u.last_login).toLocaleString() : "Never",
          u.presentation_time_limit || 25,
          u.total_sessions || 0,
          u.completed_sessions || 0,
          u.average_score || 0
        ].join(","))
      ];
      
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Users exported to CSV!");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Export ATS CSV (Name, Email, Profile URL only)
  const exportATS = async () => {
    try {
      const response = await axios.get(`${API}/users/export`);
      const userData = response.data.users;
      
      // Get base URL for profile links
      const baseUrl = window.location.origin;
      
      // Create ATS CSV content
      const headers = ["Name", "Email", "Profile URL"];
      const csvRows = [
        headers.join(","),
        ...userData.map(u => [
          `"${u.name || ''}"`,
          `"${u.email || ''}"`,
          `"${baseUrl}/u/${u.share_token}"`
        ].join(","))
      ];
      
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_ats_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("ATS file exported successfully!");
    } catch (error) {
      toast.error("ATS export failed");
    }
  };

  // Fetch API Configuration
  const fetchApiConfig = async () => {
    setLoadingApiConfig(true);
    try {
      const response = await axios.get(`${API}/admin/api-config`);
      if (response.data) {
        setApiConfig(response.data);
      }
    } catch (error) {
      console.log("No API config found, using defaults");
    } finally {
      setLoadingApiConfig(false);
    }
  };

  // Save API Configuration
  const saveApiConfig = async () => {
    try {
      await axios.post(`${API}/admin/api-config`, apiConfig);
      toast.success("API configuration saved!");
    } catch (error) {
      toast.error("Failed to save API configuration");
    }
  };

  // Sync users from external API
  const syncUsersFromAPI = async () => {
    if (!apiConfig.endpoint_url) {
      toast.error("Please configure the API endpoint first");
      return;
    }
    
    setSyncingUsers(true);
    try {
      const response = await axios.post(`${API}/admin/sync-users-from-api`);
      toast.success(`Synced ${response.data.synced_count} users from external API`);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync users");
    } finally {
      setSyncingUsers(false);
    }
  };

  // Test API Connection
  const testApiConnection = async () => {
    if (!apiConfig.endpoint_url) {
      toast.error("Please enter an API endpoint URL");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/test-api-connection`, {
        endpoint_url: apiConfig.endpoint_url,
        api_key: apiConfig.api_key,
        auth_type: apiConfig.auth_type
      });
      toast.success(`Connection successful! Found ${response.data.user_count} users`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection failed");
    }
  };

  useEffect(() => {
    if (mainTab === "api-config") {
      fetchApiConfig();
    }
    if (mainTab === "evolve-api") {
      fetchEvolveApiConfig();
    }
  }, [mainTab]);

  // EVOLVE API Functions
  const fetchEvolveApiConfig = async () => {
    setLoadingEvolveApi(true);
    try {
      const response = await axios.get(`${API}/admin/evolve-api-config`);
      setEvolveApi(response.data);
    } catch (error) {
      console.log("No EVOLVE API config found");
    } finally {
      setLoadingEvolveApi(false);
    }
  };

  const generateApiKey = async () => {
    if (!newApiKeyName.trim()) {
      toast.error("Please enter a name for the API key");
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/evolve-api-keys`, {
        name: newApiKeyName.trim()
      });
      setGeneratedKey(response.data);
      setNewApiKeyName("");
      fetchEvolveApiConfig();
      toast.success("API key generated successfully!");
    } catch (error) {
      toast.error("Failed to generate API key");
    }
  };

  const revokeApiKey = async (keyId) => {
    try {
      await axios.delete(`${API}/admin/evolve-api-keys/${keyId}`);
      fetchEvolveApiConfig();
      toast.success("API key revoked");
    } catch (error) {
      toast.error("Failed to revoke API key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Get base URL for API documentation
  const getApiBaseUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  };

  // Get unique departments for filter
  const departments = [...new Set(users.map(u => u.department))].filter(Boolean);

  // Format last login date
  const formatLastLogin = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(user =>
      (user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
       user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
       user.department.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (filterDepartment === "all" || user.department === filterDepartment) &&
      (filterStatus === "all" || 
       (filterStatus === "active" && user.is_active) || 
       (filterStatus === "inactive" && !user.is_active))
    )
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "department":
          aVal = a.department.toLowerCase();
          bVal = b.department.toLowerCase();
          break;
        case "last_login":
          aVal = a.last_login ? new Date(a.last_login) : new Date(0);
          bVal = b.last_login ? new Date(b.last_login) : new Date(0);
          break;
        case "sessions":
          aVal = a.sessions_completed || 0;
          bVal = b.sessions_completed || 0;
          break;
        case "score":
          aVal = a.average_score || 0;
          bVal = b.average_score || 0;
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Manage employees and their access
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={exportATS}
            className="rounded-full"
            title="Export for ATS systems"
          >
            <FileUser className="w-4 h-4 mr-2" />
            Export ATS
          </Button>
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="rounded-full"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            data-testid="add-user-btn"
            onClick={() => {
              setSelectedUser(null);
              setFormData({ name: "", email: "", department: "", role: "user", presentation_time_limit: 25, designation: "", tek_id: "", location: "" });
              setShowDialog(true);
            }}
            className="bg-primary hover:bg-primary/90 rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Main Tabs: Users / API Configuration / EVOLVE API */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setMainTab("users")}
          className={`px-4 py-2 font-medium transition-colors ${
            mainTab === "users"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <UserCheck className="w-4 h-4 inline mr-2" />
          Users
        </button>
        <button
          onClick={() => setMainTab("api-config")}
          className={`px-4 py-2 font-medium transition-colors ${
            mainTab === "api-config"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Import API
        </button>
        <button
          onClick={() => setMainTab("evolve-api")}
          className={`px-4 py-2 font-medium transition-colors ${
            mainTab === "evolve-api"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          EVOLVE API
        </button>
      </div>

      {/* EVOLVE API Tab Content */}
      {mainTab === "evolve-api" && (
        <div className="space-y-6">
          {/* API Keys Management */}
          <Card className="stats-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary" />
                API Credentials
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Generate API keys for external systems to access EVOLVE user data
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingEvolveApi ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Generate New Key */}
                  <div className="flex gap-3">
                    <Input
                      placeholder="API Key Name (e.g., HR System, ATS Integration)"
                      value={newApiKeyName}
                      onChange={(e) => setNewApiKeyName(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={generateApiKey} className="rounded-full bg-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Generate Key
                    </Button>
                  </div>

                  {/* Newly Generated Key Alert */}
                  {generatedKey && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                      <div className="flex items-center gap-2 text-green-700 font-medium">
                        <CheckSquare className="w-5 h-5" />
                        API Key Generated Successfully!
                      </div>
                      <p className="text-sm text-green-600">
                        Copy this key now. You won&apos;t be able to see it again.
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-3 bg-white rounded border text-sm font-mono break-all">
                          {generatedKey.api_key}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(generatedKey.api_key)}
                          className="rounded-full"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setGeneratedKey(null)}
                        className="text-green-700"
                      >
                        Dismiss
                      </Button>
                    </div>
                  )}

                  {/* Existing Keys */}
                  {evolveApi.api_keys && evolveApi.api_keys.length > 0 ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Active API Keys</Label>
                      <div className="space-y-2">
                        {evolveApi.api_keys.map((key) => (
                          <div key={key.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div>
                              <p className="font-medium">{key.name}</p>
                              <p className="text-xs text-muted-foreground">
                                Created: {new Date(key.created_at).toLocaleDateString()} • 
                                Key: {key.key_preview}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => revokeApiKey(key.id)}
                              className="rounded-full text-red-500 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No API keys generated yet. Create one to enable external API access.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* API Documentation */}
          <Card className="stats-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                API Documentation
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Use these endpoints to integrate EVOLVE with your systems
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Base URL */}
              <div className="space-y-2">
                <Label className="font-medium">Base URL</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted rounded text-sm font-mono">
                    {getApiBaseUrl()}/api/external
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`${getApiBaseUrl()}/api/external`)}
                    className="rounded-full"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Authentication */}
              <div className="space-y-2">
                <Label className="font-medium">Authentication</Label>
                <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                  <p className="text-sm">Include your API key in the request header:</p>
                  <code className="block p-2 bg-white rounded text-sm font-mono">
                    X-API-Key: your_api_key_here
                  </code>
                </div>
              </div>

              {/* Endpoints */}
              <div className="space-y-4">
                <Label className="font-medium">Available Endpoints</Label>
                
                {/* GET /users */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">GET</span>
                    <code className="font-mono text-sm">/users</code>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Get all active users with their profile URLs
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Example Request:</p>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`curl -X GET "${getApiBaseUrl()}/api/external/users" \\
  -H "X-API-Key: your_api_key"`}
                    </pre>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Response:</p>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`{
  "users": [
    {
      "id": "uuid",
      "name": "John Doe",
      "email": "john@example.com",
      "department": "Engineering",
      "designation": "Senior Developer",
      "tek_id": "TEK001",
      "location": "New York",
      "profile_url": "${window.location.origin}/u/abc123",
      "is_active": true,
      "sessions_completed": 5,
      "average_score": 78.5
    }
  ],
  "total": 1
}`}
                    </pre>
                  </div>
                </div>

                {/* GET /users/:id */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">GET</span>
                    <code className="font-mono text-sm">/users/:id</code>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Get a specific user by ID or email
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Example Request:</p>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`curl -X GET "${getApiBaseUrl()}/api/external/users/john@example.com" \\
  -H "X-API-Key: your_api_key"`}
                    </pre>
                  </div>
                </div>

                {/* GET /users/profile-urls */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">GET</span>
                    <code className="font-mono text-sm">/users/profile-urls</code>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Get a simple list of all users with their profile URLs (ATS format)
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Response:</p>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`{
  "users": [
    {
      "name": "John Doe",
      "email": "john@example.com",
      "profile_url": "${window.location.origin}/u/abc123"
    }
  ],
  "total": 1
}`}
                    </pre>
                  </div>
                </div>

                {/* POST /users */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold">POST</span>
                    <code className="font-mono text-sm">/users</code>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Create a new user and get their profile URL
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Request Body:</p>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`{
  "name": "Jane Smith",
  "email": "jane@example.com",
  "department": "Marketing",
  "designation": "Marketing Manager",
  "tek_id": "TEK002",
  "location": "Chicago"
}`}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Rate Limits */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Rate Limits:</strong> 100 requests per minute per API key. 
                  Contact administrator if you need higher limits.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* API Configuration Tab Content */}
      {mainTab === "api-config" && (
        <Card className="stats-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              External API Integration
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure an external API to sync user data from another system (HR, ERP, etc.)
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {loadingApiConfig ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Enable/Disable */}
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="api-enabled"
                    checked={apiConfig.enabled}
                    onCheckedChange={(checked) => setApiConfig({ ...apiConfig, enabled: checked })}
                  />
                  <Label htmlFor="api-enabled" className="font-medium">
                    Enable External API Integration
                  </Label>
                </div>

                {/* API Endpoint Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="endpoint_url">API Endpoint URL *</Label>
                    <Input
                      id="endpoint_url"
                      placeholder="https://api.yourcompany.com/users"
                      value={apiConfig.endpoint_url}
                      onChange={(e) => setApiConfig({ ...apiConfig, endpoint_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      The URL that returns user data in JSON format
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="auth_type">Authentication Type</Label>
                    <Select
                      value={apiConfig.auth_type}
                      onValueChange={(value) => setApiConfig({ ...apiConfig, auth_type: value })}
                    >
                      <SelectTrigger id="auth_type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bearer">Bearer Token</SelectItem>
                        <SelectItem value="api_key">API Key (Header)</SelectItem>
                        <SelectItem value="basic">Basic Auth</SelectItem>
                        <SelectItem value="none">No Authentication</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="api_key">API Key / Token</Label>
                    <Input
                      id="api_key"
                      type="password"
                      placeholder="Enter your API key or token"
                      value={apiConfig.api_key}
                      onChange={(e) => setApiConfig({ ...apiConfig, api_key: e.target.value })}
                    />
                  </div>
                </div>

                {/* Field Mapping */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Field Mapping</Label>
                    <p className="text-sm text-muted-foreground">
                      Map fields from your external API to EVOLVE user fields
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Name Field</Label>
                      <Input
                        placeholder="name"
                        value={apiConfig.field_mapping.name}
                        onChange={(e) => setApiConfig({
                          ...apiConfig,
                          field_mapping: { ...apiConfig.field_mapping, name: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Email Field</Label>
                      <Input
                        placeholder="email"
                        value={apiConfig.field_mapping.email}
                        onChange={(e) => setApiConfig({
                          ...apiConfig,
                          field_mapping: { ...apiConfig.field_mapping, email: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Department Field</Label>
                      <Input
                        placeholder="department"
                        value={apiConfig.field_mapping.department}
                        onChange={(e) => setApiConfig({
                          ...apiConfig,
                          field_mapping: { ...apiConfig.field_mapping, department: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Designation/Title Field</Label>
                      <Input
                        placeholder="title"
                        value={apiConfig.field_mapping.designation}
                        onChange={(e) => setApiConfig({
                          ...apiConfig,
                          field_mapping: { ...apiConfig.field_mapping, designation: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">TEK ID / Employee ID Field</Label>
                      <Input
                        placeholder="employee_id"
                        value={apiConfig.field_mapping.tek_id}
                        onChange={(e) => setApiConfig({
                          ...apiConfig,
                          field_mapping: { ...apiConfig.field_mapping, tek_id: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Location Field</Label>
                      <Input
                        placeholder="location"
                        value={apiConfig.field_mapping.location}
                        onChange={(e) => setApiConfig({
                          ...apiConfig,
                          field_mapping: { ...apiConfig.field_mapping, location: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Sync Options */}
                <div className="space-y-2">
                  <Label>Sync Frequency</Label>
                  <Select
                    value={apiConfig.sync_interval}
                    onValueChange={(value) => setApiConfig({ ...apiConfig, sync_interval: value })}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Only</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Last Sync Info */}
                {apiConfig.last_sync && (
                  <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                    Last synced: {new Date(apiConfig.last_sync).toLocaleString()}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={testApiConnection}
                    className="rounded-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Test Connection
                  </Button>
                  <Button
                    onClick={saveApiConfig}
                    className="rounded-full bg-primary"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Save Configuration
                  </Button>
                  <Button
                    variant="default"
                    onClick={syncUsersFromAPI}
                    disabled={syncingUsers || !apiConfig.enabled}
                    className="rounded-full bg-[#059669] hover:bg-[#059669]/90"
                  >
                    {syncingUsers ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sync Users Now
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Users Tab Content */}
      {mainTab === "users" && (
        <>
      {/* Bulk Action Bar */}
      {selectedUsers.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-primary/10 rounded-xl border border-primary/20">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-primary" />
            <span className="font-medium">{selectedUsers.length} user(s) selected</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkAction("activate")}
              className="rounded-full text-[#059669] border-[#059669] hover:bg-[#059669]/10"
            >
              <UserCheck className="w-4 h-4 mr-1" />
              Activate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkAction("deactivate")}
              className="rounded-full text-[#D97706] border-[#D97706] hover:bg-[#D97706]/10"
            >
              <UserX className="w-4 h-4 mr-1" />
              Deactivate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkAction("delete")}
              className="rounded-full text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626]/10"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedUsers([])}
              className="rounded-full"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Tabs for Active/Deleted Users */}
      <div className="flex gap-2 border-b border-border pb-2">
        <Button
          variant={activeTab === "active" ? "default" : "ghost"}
          onClick={() => { setActiveTab("active"); setSelectedUsers([]); }}
          className={`rounded-full ${activeTab === "active" ? "bg-primary" : ""}`}
        >
          Active Users ({users.length})
        </Button>
        <Button
          variant={activeTab === "deleted" ? "default" : "ghost"}
          onClick={() => setActiveTab("deleted")}
          className={`rounded-full ${activeTab === "deleted" ? "bg-[#DC2626]" : ""}`}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Deleted ({deletedUsers.length})
        </Button>
      </div>

      {activeTab === "active" ? (
      <Card className="stats-card">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold">All Users ({filteredUsers.length})</CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="search-users"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-muted-foreground">Filters:</span>
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground ml-4">Sort:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="last_login">Last Login</SelectItem>
                  <SelectItem value="sessions">Sessions</SelectItem>
                  <SelectItem value="score">Score</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                className="h-9"
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                {sortOrder === "asc" ? "Asc" : "Desc"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-10">
                      <Checkbox
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>
                      <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("last_login")}>
                        <Clock className="w-3 h-3" />
                        Last Login
                        {sortBy === "last_login" && <ArrowUpDown className="w-3 h-3" />}
                      </div>
                    </th>
                    <th>
                      <div className="flex items-center gap-1">
                        <Timer className="w-3 h-3" />
                        Time Limit
                      </div>
                    </th>
                    <th>Status</th>
                    <th>Sessions</th>
                    <th>Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`} className={selectedUsers.includes(user.id) ? "bg-primary/5" : ""}>
                      <td>
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={() => toggleSelectUser(user.id)}
                        />
                      </td>
                      <td>
                        <Link to={`/admin/users/${user.id}`} className="font-medium text-primary hover:underline">
                          {user.name}
                        </Link>
                      </td>
                      <td className="text-[#5C5C5C] text-sm">{user.email}</td>
                      <td>{user.department}</td>
                      <td className="text-[#5C5C5C] text-sm">
                        {formatLastLogin(user.last_login)}
                      </td>
                      <td>
                        <span className="text-sm font-medium">
                          {user.presentation_time_limit || 25} min
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{user.sessions_completed}</td>
                      <td>{user.average_score || '-'}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`copy-link-${user.id}`}
                            onClick={() => copyUserLink(user)}
                            className="h-8 w-8"
                            title="Copy Profile Link"
                          >
                            <Link2 className="w-4 h-4 text-[#2563EB]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-user-${user.id}`}
                            onClick={() => handleEdit(user)}
                            className="h-8 w-8"
                            title="Edit User"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`activity-user-${user.id}`}
                            onClick={() => viewUserActivity(user)}
                            className="h-8 w-8"
                            title="View Activity Log"
                          >
                            <Activity className="w-4 h-4 text-[#8B5CF6]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`toggle-user-${user.id}`}
                            onClick={() => toggleStatus(user)}
                            className="h-8 w-8"
                            title={user.is_active ? "Deactivate" : "Activate"}
                          >
                            {user.is_active ? (
                              <UserX className="w-4 h-4 text-[#DC2626]" />
                            ) : (
                              <UserCheck className="w-4 h-4 text-[#059669]" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-user-${user.id}`}
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteDialog(true);
                            }}
                            className="h-8 w-8 text-[#DC2626] hover:text-[#DC2626]"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Link to={`/admin/users/${user.id}`}>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`view-user-${user.id}`}
                              className="h-8 w-8"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state py-12">
              <p className="text-[#5C5C5C]">No users found</p>
            </div>
          )}
        </CardContent>
      </Card>
      ) : (
      /* Deleted Users Tab */
      <Card className="stats-card border-[#DC2626]/20">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-[#DC2626]" />
            Deleted Users ({deletedUsers.length})
          </CardTitle>
          <p className="text-sm text-[#5C5C5C] mt-1">
            Users that have been deleted can be restored or permanently removed.
          </p>
        </CardHeader>
        <CardContent>
          {deletedUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Deleted On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedUsers.map((user) => (
                    <tr key={user.id} className="opacity-70">
                      <td className="font-medium">{user.name}</td>
                      <td className="text-[#5C5C5C] text-sm">{user.email}</td>
                      <td>{user.department}</td>
                      <td className="text-[#5C5C5C] text-sm">
                        {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => restoreUser(user)}
                            className="rounded-full text-[#059669] border-[#059669] hover:bg-[#059669]/10"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => permanentDelete(user)}
                            className="rounded-full text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626]/10"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Forever
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state py-12 text-center">
              <Trash2 className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
              <p className="text-[#5C5C5C]">No deleted users</p>
              <p className="text-sm text-[#9CA3AF]">Deleted users will appear here for recovery</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}
      </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser ? 'Update user details below.' : 'Fill in the details to create a new user.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <Label htmlFor="tek_id">TEK ID</Label>
                <Input
                  id="tek_id"
                  data-testid="user-tek-id-input"
                  value={formData.tek_id}
                  onChange={(e) => setFormData({ ...formData, tek_id: e.target.value })}
                  placeholder="TEK001234"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="user-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.doe@company.com"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="designation">Designation</Label>
                <Input
                  id="designation"
                  data-testid="user-designation-input"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="Senior Manager"
                />
              </div>
              <div>
                <Label htmlFor="department">Department *</Label>
                <Input
                  id="department"
                  data-testid="user-department-input"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Sales"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                data-testid="user-location-input"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Bangalore, India"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="presentation_time_limit">
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4" />
                    Time Limit (min)
                  </div>
                </Label>
                <Input
                  id="presentation_time_limit"
                  type="number"
                  min="5"
                  max="120"
                  data-testid="user-time-limit-input"
                  value={formData.presentation_time_limit}
                  onChange={(e) => setFormData({ ...formData, presentation_time_limit: parseInt(e.target.value) || 25 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-user-btn" className="bg-primary hover:bg-primary/90">
                {selectedUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move User to Deleted List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedUser?.name}? The user will be moved to the deleted list where you can restore them later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-[#DC2626] hover:bg-[#DC2626]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Activity Log Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#8B5CF6]" />
              Activity Log - {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Recent activities for this user
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            {loadingActivity ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activityLogs.length > 0 ? (
              <div className="space-y-3">
                {activityLogs.map((log, index) => (
                  <div
                    key={log.id || index}
                    className="flex items-start gap-3 p-3 bg-[#F9FAFB] rounded-lg"
                  >
                    <div className={`p-1.5 rounded-full flex-shrink-0 ${
                      log.action_type === 'login' ? 'bg-[#2563EB]/10' :
                      log.action_type === 'session_created' ? 'bg-[#8B5CF6]/10' :
                      log.action_type === 'session_completed' ? 'bg-[#059669]/10' :
                      'bg-[#5C5C5C]/10'
                    }`}>
                      <Activity className={`w-3 h-3 ${
                        log.action_type === 'login' ? 'text-[#2563EB]' :
                        log.action_type === 'session_created' ? 'text-[#8B5CF6]' :
                        log.action_type === 'session_completed' ? 'text-[#059669]' :
                        'text-[#5C5C5C]'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A1A1A] capitalize">
                        {log.action_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-[#5C5C5C] truncate">
                        {log.description}
                      </p>
                    </div>
                    <div className="text-xs text-[#9CA3AF] flex-shrink-0">
                      {formatActivityTime(log.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
                <p className="text-[#5C5C5C]">No activities recorded yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
